import { getMe } from "@/shared/providers/api";
import type { SessionUser } from "@/shared/types";
import {
  clearAccessToken,
  setAccessToken,
} from "@/shared/utils/localStorageHelpers";
import { useWalletAddress } from "@filedgr/web-core/auth";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { isMockAuthEnabled, restoreMockSession } from "@/mocks/session";
import { GlobalSelectors } from "./selectors";
import { registerLogoutHandler } from "./sessionBridge";
import { ensureFiledgrUser } from "@/shared/providers/api";
import { globalActions } from "./slice";
import { useWeb3Auth } from "./Web3AuthProvider";

/**
 * Renders nothing. Mirrors the Web3Auth context into the redux `global` slice
 * and keeps the stored idToken — which the API client attaches as
 * `Authorization: Bearer` — in step with the live session.
 *
 * The shared provider (@filedgr/web-core/auth) hands back Web3Auth's raw,
 * normalized claims; the app-shaped `SessionUser` is assembled here. The wallet
 * address is read separately (`useWalletAddress`) and lands a tick later than
 * the rest of the session — `setSession` in the slice is careful not to let
 * that second update clobber the server-resolved `isAdmin`.
 *
 * Mounted once, inside `<Web3AuthProvider>` and above `<App />`.
 */
export const GlobalProvider = () => {
  const dispatch = useDispatch();
  const sessionExists = useSelector(GlobalSelectors.isAuthenticated);

  // Mock mode never reaches Web3Auth: it cannot initialise for an origin that
  // is not whitelisted (PROVISIONING §6), which is every developer machine.
  // See `src/mocks/session.ts` — dead code in any other build.
  React.useEffect(() => {
    if (!isMockAuthEnabled) return;
    const user = restoreMockSession();
    dispatch(
      globalActions.setSession({
        user,
        isAuthenticated: Boolean(user),
        isLoading: false,
        error: null,
      })
    );
  }, [dispatch]);

  const web3auth = useWeb3Auth();
  const walletAddress = useWalletAddress();

  const isLoading = web3auth?.isLoading ?? true;
  const isAuthenticated = web3auth?.isAuthenticated ?? false;
  const claims = web3auth?.user ?? null;
  const logout = web3auth?.logout;

  // The shared provider merges a freshly minted identity token into the user
  // claims (both on login and on refresh-restore), so this is the live token.
  const idToken: string | null = claims?.idToken ?? null;

  const user = React.useMemo<SessionUser | null>(() => {
    if (!claims) return null;
    return {
      email: claims.email ?? "",
      walletAddress,
      name: claims.name ?? null,
      profileImage: claims.profileImage ?? null,
      // Admin is decided server-side from ADMIN_EMAILS (SPEC §6). The client
      // starts pessimistic and `setIsAdmin` promotes it once the server has
      // actually served an admin response.
      isAdmin: false,
    };
  }, [claims, walletAddress]);

  React.useEffect(() => {
    // In mock mode this would clear the token `signInMock()` just wrote.
    if (isMockAuthEnabled) return;
    if (isAuthenticated && idToken) {
      setAccessToken(idToken);
    } else if (!isAuthenticated && !isLoading) {
      clearAccessToken();
    }
  }, [isAuthenticated, idToken, isLoading]);

  // Provision this buyer on Filedgr exactly once per signed-in session.
  //
  // `POST /api/me/filedgr-user` forwards our idToken to the platform's `GET /users`,
  // which CREATES the account when it has never seen this identity. The viewer grant
  // identifies the buyer by email and cannot grant to an unknown user, so without this
  // a first-time buyer could pay and then have the grant fail terminally with
  // user-not-found — money taken, no access, no retry that helps.
  //
  // filedgr-web-app does the same thing implicitly: its global saga calls
  // `getUserData()` right after login, which is why dashboard users simply exist.
  //
  // Runs after the token is set, because the request needs the Authorization header.
  // Deliberately swallows failures: this is a warm-up, the server logs the error, and
  // the checkout route makes the same call again as a last line of defence before any
  // money moves.
  const provisionedFor = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (isMockAuthEnabled) return;
    if (!isAuthenticated || !idToken) return;
    const key = user?.email ?? idToken.slice(-16);
    if (provisionedFor.current === key) return;
    provisionedFor.current = key;
    void ensureFiledgrUser().catch(() => undefined);
  }, [isAuthenticated, idToken, user?.email]);

  React.useEffect(() => {
    // Web3Auth never initialises in mock mode, so it reports "not signed in"
    // for ever — and this effect runs after the mock restore above, so it used
    // to overwrite the restored session with an empty one. The result was a
    // login gate on every auth-gated screen despite `kf.mock.signedIn` being
    // true: exactly the screens the mock exists to make reachable. The mock
    // effect owns the session in mock mode; this one must not touch it.
    if (isMockAuthEnabled) return;
    dispatch(
      // The shared provider reports failures to the console rather than through
      // the context, so there is no error to mirror here.
      globalActions.setSession({ user, isAuthenticated, isLoading, error: null })
    );
  }, [dispatch, user, isAuthenticated, isLoading]);

  // Let the global saga tear the session down (401/403, expired token). The
  // shared provider owns `logout`, so the bridge is registered from here.
  React.useEffect(() => {
    if (!logout) return;
    registerLogoutHandler(logout);
    return () => registerLogoutHandler(null);
  }, [logout]);

  // Resolve admin status as soon as a session exists, so the Operations link is
  // correct on first paint instead of appearing only after someone guesses the
  // /admin URL. `ADMIN_EMAILS` is a server secret, so `GET /api/me` is the only
  // authority (SPEC §10.1) — this cannot be derived client-side.
  //
  // Deliberately fire-and-forget: a failure here means "not known to be an
  // admin", which is already the default. The /admin screen does its own,
  // stricter check and distinguishes a real 403 from a transport failure, so
  // nothing here needs to surface an error.
  //
  // Read from the store rather than from Web3Auth directly, so this also runs
  // for a mock session — otherwise Operations would never appear in the account
  // menu on a developer's machine.
  React.useEffect(() => {
    if (!sessionExists) return;

    let cancelled = false;
    void (async () => {
      try {
        const me = await getMe();
        if (!cancelled) dispatch(globalActions.setIsAdmin(me.isAdmin));
      } catch {
        /* stays false — see above */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, sessionExists]);

  return null;
};

export { useSession } from "./useSession";
export { useWeb3Auth, Web3AuthProvider } from "./Web3AuthProvider";
